/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Component } from 'react';
import axios from 'axios';
import qs from 'qs';
import { Multiselect } from 'react-widgets';
import { RadioButton } from '../ui/BinaryRadioButton';
import Button from '../../ui/Button';
import colors from '../../../css/colors';
import getBase64 from '../../FileUploads';
import { showNextButton, getNextLink } from '../FormStepperFunctions';
import { selectStyles } from '../SharedFormStyles';
import withProperty from '../WithProperty';
import CurrencyField from '../../ui/CurrencyField';
import { Checkbox, Icon, Loader } from 'semantic-ui-react';
import WithDocumentStation from '../ui/DocumentStation/WithDocumentStation';
import SubmitFormButton from '../SubmitFormButton';
import withUser from 'lib/WithUser';
import Select from 'react-select';
import ReusableTopOfForm from '../ReusableTopOfForm';
import { AutomateNames } from './Components/AutomateNames';
import { InfoFoundSection } from './Components/InfoFoundSection';
import NameSearchUploadAttachments from '../NameSearchUploadAttachments/NameSearchUploadAttachments';

const searchTypeId = 35;

const customStyles = {
  container: (provided, state) => ({
    ...provided,
    width: 296,
  }),
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${colors.grey}`,
    boxShadow: state.isFocused && '1px solid #ff5252',
    border: state.isFocused && '1px solid #ff5252',
    height: 48,
    '&:hover': {
      border: state.isFocused && '1px solid #ff5252',
    },
  }),
};

class BankruptcySearch extends Component {
  state = {
    nameOptions: [],
    names: [],
    attachedFiles: [],
    loaderStatus: false,
    selectedCounty: {},
    selectAllNames: false,
    doNotCheck: false,
    searchId: 0,
    loadingNames: false,
    countyOptions: [],
    openBkModal: false,
    bkReportInfo: {},
    documentKey: 0,
    namesToAutomate: [
      {
        firstName: null,
        middleName: null,
        lastName: null,
        ran: false,
        loading: false,
        failed: false,
        id: Math.random(),
      },
    ],
  };

  componentDidMount = async () => {
    axios
      .get(`/api/searches/GetSearchByType/${searchTypeId}/${+this.props.match.params.propertyid}`)
      .then(({ data }) => {
        this.props.setSearchId(data.id);
        this.setState({ searchId: data.id });
      });
    const queryString = qs.stringify({ searchType: 'Bankruptcy' });
    this.setState({ loadingNames: true });
    const { data: savedData } = await axios.get(
      `/api/property/GetPropertyContacts/${+this.props.match.params.propertyid}?${queryString}`
    );
    const { data: property } = await axios.get(
      `/api/property/getProperty/${this.props.match.params.propertyid}`
    );
    const { data: countiesResponse } = await axios.get(`/api/municipalities/GetAllCounties`);
    const countyOptions = countiesResponse
      .filter(c => c.bankruptcyDistrict)
      .map(c => ({ ...c, value: c.Id, label: `${c.name} - ${c.bankruptcyDistrict}` }));
    const selectedCounty = countyOptions.find(c => c.id === property.countyId) || {};

    this.setState({
      nameOptions: savedData.map(s => ({ ...s, infoFound: false })),
      selectedCounty: { ...selectedCounty, stateCode: property.stateCode },
      loadingNames: false,
      countyOptions,
    });
  };

  onFilesAdded = files => {
    this.setState({ attachedFiles: [...this.state.attachedFiles, ...files] });
  };

  onFileRemoved = file => {
    this.setState({
      attachedFiles: this.state.attachedFiles.filter(f => f !== file),
    });
  };

  handlePreview = async () => {
    const { data: html } = await axios.post('/api/searches/CreatePDF', {
      template: 'namesearchtemplate',
      data: await this.getFormContent(),
    });
    this.setState({ pdfHtml: html });
  };

  handleDownload = async () => {
    this.setState({ loaderStatus: true });
   
    await axios.post('/api/searches/DownloadPDF', {
      template: 'namesearchtemplate',
      data: await this.getFormContent()
    });

    const nextLink = getNextLink(
      this.props.property.orderId,
      searchTypeId,
      this.props.match.params.propertyid
    );
    if (nextLink == null) {
      this.props.history.push(`/orders/order-information/${this.props.property.orderId}`);
    } else {
      this.props.history.push(nextLink);
    }
    this.setState({ loaderStatus: false });
  };

  getAdditionalInfoText(county) {
    const baseInfo = 'U.S. Bankruptcy Court,';
    if (county?.stateCode === 'NJ') {
      return `${baseInfo} District of New Jersey`;
    }

    return `${baseInfo} ${county?.bankruptcyDistrict}ern District`;
  }

  getFormContent = async () => {
    const { attachedFiles, names, doNotCheck } = this.state;

    const promises = attachedFiles.map(async f => {
      const base64 = await getBase64(f);
      return { name: f.name, base64String: base64 };
    });
    const files = await Promise.all(promises);
    return {
      date: new Date(),
      searchType: 'Bankruptcy',
      noText:
        'There is no record of an open bankruptcy filing for the above mentioned individual, corporation or business.',
      yesText: 'The following information has been found: (See attached)',
      doNotCheck,
      additionalInfoTitle: 'The following office(s) have been checked:',
      additionalInfo: this.getAdditionalInfoText(this.state.selectedCounty),
      countyOverride: this.state.selectedCounty?.name,
      names,
      propertyId: +this.props.match.params.propertyid,
      searchTypeId,
      searchDocumentType: 'Bankruptcy Attachment',
      attachments: files,
      namesToAutomate: this.state.namesToAutomate,
    };
  };

  handleCreate(name) {
    const { nameOptions, names } = this.state;

    const newOption = {
      name,
      id: nameOptions.length + 1,
      infoFound: false,
    };

    this.setState({
      names: [...names, newOption],
      nameOptions: [...nameOptions, newOption],
    });
  }

  render() {
    const { names, nameOptions, pdfHtml, loaderStatus, doNotCheck } = this.state;
    const showNext = showNextButton(this.props.match.params.orderId, searchTypeId);
    const params = new URLSearchParams(this.props.location.search);
    const isEdit = params.get('isEdit');
    return (
      <div css={{ display: 'flex', height: '100%' }}>
        <div css={{ width: 654 }}>
          <ReusableTopOfForm
            handlePreview={this.handlePreview}
            searchHeader="Bankruptcy Search"
            propertyId={+this.props.match.params.propertyid}
            searchTypeId={searchTypeId}
            property={this.props.property}
            getFormContent={this.getFormContent}
            template="namesearchtemplate"
            searchId={this.state.searchId}
          />
          <div css={{ marginBottom: 32 }}>
            <span
              css={{
                marginBottom: 8,
                fontWeight: 'bold',
                display: 'inline-block',
              }}
            >
              Names
            </span>
            {this.state.loadingNames ? (
              <div>
                <Loader active inline size="small" />
              </div>
            ) : (
              <RadioButton
                checked={this.state.selectAllNames}
                text="Select All Names"
                addedStyles={{ marginBottom: 10 }}
                onChange={() => {
                  const stateValue = !this.state.selectAllNames;
                  if (stateValue) {
                    this.setState({ names: nameOptions });
                  }
                  this.setState({ selectAllNames: stateValue });
                }}
              />
            )}
            <div css={selectStyles}>
              <Multiselect
                value={names}
                data={nameOptions}
                onChange={editedNames => this.setState({ names: editedNames })}
                onCreate={name => this.handleCreate(name)}
                allowCreate="onFilter"
                valueField="id"
                textField="name"
                tagComponent={({ item }) => {
                  return (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div>{item.name}</div>
                      <Icon
                        style={{ cursor: 'pointer' }}
                        name="copy outline"
                        onClick={() => {
                          navigator.clipboard.writeText(item.name);
                        }}
                      />
                    </div>
                  );
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
              <Checkbox
                style={{ marginTop: 32 }}
                checked={doNotCheck}
                label={'Do not check'}
                onChange={() => this.setState({ doNotCheck: !doNotCheck })}
              />
              {this.state.names.length > 0 && (
                <NameSearchUploadAttachments
                  names={names}
                  open={this.state.uploadAttachmentModal}
                  afterSubmit={newNames => {
                    this.setState({
                      names: newNames,
                      documentKey: this.state.documentKey + 1,
                    });
                  }}
                  searchId={this.state.searchId}
                  searchTypeId={searchTypeId}
                />
              )}
            </div>
            {!doNotCheck && names?.length > 0 && (
              <InfoFoundSection
                headerText={'Have any open bankruptcies been found?'}
                names={names}
                onAllYesCheck={() => this.setState({ names: names.map(n => ({ ...n, infoFound: true })) })}
                onAllNoCheck={() => this.setState({ names: names.map(n => ({ ...n, infoFound: false })) })}
                onChangeYes={n =>
                  this.setState({
                    names: names.map(d => (d.id === n.id ? { ...d, infoFound: true } : d)),
                  })
                }
                onChangeNo={n =>
                  this.setState({
                    names: names.map(d => (d.id === n.id ? { ...d, infoFound: false } : d)),
                  })
                }
              />
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 32, flexDirection: 'column' }}>
              <div>Bankruptcy County/District</div>
              <Select
                options={this.state.countyOptions}
                value={this.state.selectedCounty}
                onChange={selectedCounty => this.setState({ selectedCounty })}
                styles={customStyles}
              />
            </div>
            <AutomateNames
              bkDistrict={
                this.state.selectedCounty?.label?.split('-')?.length > 1
                  ? this.state.selectedCounty.label.split('-')[1].trim()
                  : undefined
              }
              searchTypeId={searchTypeId}
              orderId={this.props.property.orderId}
              propertyId={this.props.property.id}
              searchId={this.state.searchId}
            />
          </div>

          <div css={{ marginTop: 43, display: 'flex', justifyContent: 'flex-end' }}>
            <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                secondary
                onClick={() =>
                  this.props.history.push(`/orders/order-information/${this.props.property.orderId}`)
                }
                disabled={loaderStatus}
              >
                Cancel
              </Button>
              {/* <div css={{ marginLeft: 8 }}>
                <Button onClick={this.handleDownload} loadingStatus={loaderStatus}>
                  {showNext ? 'Next' : `${isEdit ? 'Save' : 'Submit'}`}
                </Button>
              </div> */}
              <div css={{ marginLeft: 8 }}>
                <SubmitFormButton
                  searchTypeId={searchTypeId}
                  onSubmit={this.handleDownload}
                  reactFormal={false}
                  userId={this.props.user.id}
                  orderId={this.props.property.orderId}
                  loadingStatus={loaderStatus}
                  buttonText={showNext ? 'Next' : `${isEdit ? 'Save' : 'Submit'}`}
                  propertyId={+this.props.match.params.propertyid}
                  getFormContent={this.getFormContent}
                />
              </div>
            </div>
          </div>
          {this.state.searchId > 0 && (
            <WithDocumentStation
              searchId={this.state.searchId}
              searchTypeId={searchTypeId}
              names={nameOptions}
              key={this.state.documentKey}
            />
          )}
        </div>
        <div css={{ width: '100%' }}>
          <iframe style={{ width: '100%', height: '100%' }} srcDoc={pdfHtml} />
        </div>
      </div>
    );
  }
}

export default withUser(withProperty(BankruptcySearch, searchTypeId, false, true));
